import { ApolloQueryResult } from '@apollo/client';
import { useI18n } from '@i18n';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ModalProps,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import debounce from '@mui/utils/debounce';
import { Fragment, RefObject, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import MenuPopover from '@/components/MenuPopover';
import { useSession } from '@/components/session/useSession';
import { ICON_SIZE_MAP } from '@/config';
import { PlannedBuildingLabel } from '@/sections/components/PlannedBuildingLabel';
import { highlightText } from '@/utils/highlightText';

export type SearchItemType = {
  /**
   * The value of the search item.
   */
  value: number;
  /**
   * The label of the search item.
   */
  label: string;
  /**
   * Optional secondary text shown below the label (e.g. Country · Portfolio · EU).
   */
  secondary?: string;
  /**
   * Indicates whether the bottom divider should be displayed.
   * When set to `true`, a visual divider is rendered at the bottom.
   */
  hasBottomDivider?: boolean;
  /** Only set for building rows when NEW_CONSTRUCTION is enabled. */
  isNewConstruction?: boolean;
  /** When `true`, missing-energy badge / non-selectable row (only when NEW_CONSTRUCTION is enabled). */
  isMissingEnergyData?: boolean;
};

type Props = {
  /**
   * Whether the search box is open.
   */
  isOpen: boolean;
  /**
   * The anchor element of the search box, which is the element that opens the search box.
   */
  anchorRef: RefObject<HTMLDivElement | HTMLButtonElement | null>;
  /**
   * The selected items.
   */
  selectedItems: SearchItemType[];
  /**
   * Callback function when an item is selected.
   */
  onItemSelected: (building: SearchItemType) => void;
  /**
   * Function to refetch data with optional variables.
   */
  refetch?: (
    variables?:
      | Partial<{
          offset: number;
          limit: number;
          text: string;
        }>
      | undefined,
  ) => Promise<ApolloQueryResult<any>>;
  /**
   * Function to load more items based on offset and search term.
   */
  loadMore: (offset: number, debouncedSearchTerm: string) => Promise<void>;
  /**
   * Indicates if data is currently being loaded.
   */
  isLoading: boolean;
  listItems: SearchItemType[];
  /**
   * Indicates if there are more items to load.
   */
  hasMore: boolean;
  /**
   * Function to set the hasMore state.
   */
  setHasMore: (hasMore: boolean) => void;
  /**
   * The debounced search term.
   */
  debouncedSearchTerm: string;
  /**
   * Function to set the debounced search term.
   */
  setDebouncedSearchTerm: (debouncedSearchTerm: string) => void;
  /**
   * The text to display when there is no data.
   */
  noDataText?: string;
  /**
   * Indicates if multiple items can be selected.
   */
  isMultiSelect?: boolean;
  /**
   * Function to clear the selected items.
   */
  handleClear?: () => void;
  /**
   * The placeholder text for the search input.
   */
  placeholder?: string;
  /**
   * The number of items to load at a time.
   */
  limit?: number;
  /**
   * Indicates if the clear button should be shown.
   */
  showClearButton?: boolean;
  /**
   * Function to hide the search box.
   */
  onHideSearchBox?: ModalProps['onClose'];
  /**
   * Function to handle the click event.
   */
  handleClick?: () => void;
  /**
   * Indicates if the apply button should be disabled.
   */
  disableApplyButton?: boolean;
  /**
   * Indicates if the new construction search should be enabled.
   */
  enableNewConstructionSearch?: boolean;
  /**
   * Overrides the popover width. Defaults to 400. Pass the anchor element's offsetWidth
   * to make the popover match the trigger element's width.
   */
  width?: number;
  /**
   * Optional action rendered at the bottom of the list with a top divider. Always visible
   * regardless of the current search term.
   */
  bottomAction?: {
    label: string;
    onClick: () => void;
  };
};

const SearchBoxWithLazyLoading = ({
  isOpen,
  anchorRef,
  selectedItems,
  onItemSelected,
  refetch,
  loadMore,
  isLoading,
  listItems,
  hasMore,
  setHasMore,
  debouncedSearchTerm,
  setDebouncedSearchTerm,
  noDataText,
  isMultiSelect = false,
  handleClear,
  placeholder,
  limit = 10,
  showClearButton = false,
  onHideSearchBox,
  handleClick,
  disableApplyButton = false,
  enableNewConstructionSearch = false,
  width = 400,
  bottomAction,
}: Props) => {
  const theme = useTheme();
  const { t } = useI18n();
  const {
    featureFlags: { NEW_CONSTRUCTION },
  } = useSession();

  const isSearchItemDisabledForMissingEnergy = useCallback(
    (item: SearchItemType, enableNewConstructionSearch: boolean) =>
      !enableNewConstructionSearch && NEW_CONSTRUCTION && Boolean(item.isMissingEnergyData),
    [NEW_CONSTRUCTION],
  );

  const [items, setItems] = useState<SearchItemType[]>(listItems);

  useEffect(() => {
    setItems(listItems);
  }, [listItems]);

  const [searchInput, setSearchInput] = useState(debouncedSearchTerm ?? ''); // For immediate input display
  const [offset, setOffset] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(-1); // Track focused list item

  const inputRef = useRef<HTMLInputElement>(null); // Create a ref for the input element

  useEffect(() => {
    if (isOpen) {
      // WHY?: the whole popover takes a bit to render and set the ref.current
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const debouncedSearch = useCallback(
    () =>
      debounce((value) => {
        setDebouncedSearchTerm(value.toLowerCase()); // Set debounced search term
        setItems([]);
        setOffset(0);
        setHasMore(true);
        void refetch?.({ offset: 0, limit, text: `%${value.trim()}%` });
      }, 300), // Reduced debounce time to 300ms for a more responsive UI
    [limit, refetch, setDebouncedSearchTerm, setHasMore],
  );

  const handleSearch = (value: string) => {
    setSearchInput(value); // Update the input state immediately
    debouncedSearch()(value); // Trigger debounced search
  };

  const onLoadMore = async () => {
    if (!hasMore) return;

    const newOffset = offset + limit;
    loadMore(newOffset, debouncedSearchTerm);
    setOffset(newOffset); // Update offset for next fetch
  };

  // Highlight search term in displayed text

  const handleBuildingChange = (_: SyntheticEvent<Element, Event>, newValue: SearchItemType) => {
    if (isSearchItemDisabledForMissingEnergy(newValue, enableNewConstructionSearch)) return;
    onItemSelected(newValue);
    const newIndex = items.findIndex((item) => item === newValue);
    setFocusedIndex(newIndex); // Keep the focus on the selected item
  };

  const moveFocusedIndex = useCallback(
    (direction: 1 | -1) => {
      if (items.length === 0) return;
      setFocusedIndex((prevIndex) => {
        const start = prevIndex < 0 ? (direction === 1 ? -1 : 0) : prevIndex;
        let idx = start;
        for (let step = 0; step <= items.length; step++) {
          idx = (idx + direction + items.length) % items.length;
          if (!isSearchItemDisabledForMissingEnergy(items[idx], enableNewConstructionSearch)) return idx;
        }
        return prevIndex;
      });
    },
    [items, isSearchItemDisabledForMissingEnergy, enableNewConstructionSearch],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveFocusedIndex(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocusedIndex(-1);
        break;

      case 'Enter':
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          const item = items[focusedIndex];
          if (!isSearchItemDisabledForMissingEnergy(item, enableNewConstructionSearch)) {
            handleBuildingChange(e as unknown as SyntheticEvent<Element, Event>, item);
            setFocusedIndex(focusedIndex);
          }
        }
        break;
      case 'Escape':
        onHideSearchBox && onHideSearchBox({}, 'escapeKeyDown');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < items.length) {
      const focusedItem = document.getElementById(`search-item-${focusedIndex}`);
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [items.length, focusedIndex]);

  return (
    <MenuPopover
      onKeyDown={handleKeyDown}
      open={isOpen}
      anchorEl={anchorRef.current}
      onClose={onHideSearchBox}
      sx={{ width, mt: 0.5, ml: 0 }}
      arrow="top-left"
      disabledArrow
      tabIndex={0}
    >
      <TextField
        inputRef={inputRef}
        variant="outlined"
        value={searchInput} // Show search input value immediately
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'text.secondary',
            '&.Mui-focused fieldset': {
              border: '1px solid',
              borderColor: 'divider',
            },
            pl: 1,
          },
          mb: 1,
        }}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <Iconify
              icon={ICONS.SEARCH}
              width={ICON_SIZE_MAP.md}
              height={ICON_SIZE_MAP.md}
              sx={{ color: 'text.secondary', mr: 1 }}
            />
          ),
          endAdornment: searchInput && (
            <IconButton
              onClick={() => {
                handleSearch('');
                inputRef.current?.focus();
              }} // Clear search input
              sx={{ mr: -1 }}
            >
              <Iconify
                icon={ICONS.CLOSE}
                width={ICON_SIZE_MAP.sm}
                height={ICON_SIZE_MAP.sm}
                sx={{ color: 'text.secondary' }}
              />
            </IconButton>
          ),
        }}
        placeholder={placeholder ?? t('General_Search')}
        onChange={(e) => handleSearch(e.target.value)} // Update search input and trigger debounce
        autoComplete="off"
      />
      <Box
        id="scrollableDiv"
        sx={{
          maxHeight: '336px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        }}
      >
        <InfiniteScroll
          dataLength={items.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            isLoading && items.length > 0 ? (
              <Stack alignContent={'center'} alignItems={'center'} width={'100%'} height={32}>
                <CircularProgress size={24} />
              </Stack>
            ) : null
          }
          scrollableTarget="scrollableDiv"
        >
          {items.length > 0 ? (
            <List>
              {items.map((item, index) => {
                const highlightedText = highlightText(item.label, debouncedSearchTerm);
                const itemDisabled = isSearchItemDisabledForMissingEnergy(item, enableNewConstructionSearch);
                const listItem = (
                  <ListItem
                    id={`search-item-${index}`}
                    disabled={itemDisabled}
                    sx={{
                      margin: theme.spacing(0.5),
                      borderRadius: '6px',
                      cursor: itemDisabled ? 'default' : 'pointer',
                      width: `calc(100% - 8px)`,
                      opacity: itemDisabled ? 0.5 : 1,
                      '&:hover': {
                        background: itemDisabled ? 'transparent' : theme.palette.action.hover,
                      },
                      background: selectedItems.some((selectedItem) => selectedItem.value === item.value)
                        ? theme.palette.action.selected
                        : '',
                      backgroundColor: focusedIndex === index ? theme.palette.action.hover : '',
                    }}
                    onClick={(e) => {
                      if (itemDisabled) return;
                      handleBuildingChange(e, item);
                    }}
                  >
                    {isMultiSelect ? (
                      <Checkbox
                        checked={selectedItems.some((selectedItem) => selectedItem.value === item.value)}
                        size={'small'}
                      />
                    ) : null}

                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PTypography
                            variant="body2"
                            noOverflow={{
                              maxWidth: 'inherit',
                              title: typeof highlightedText === 'string' ? highlightedText : '',
                            }}
                          >
                            {highlightedText}
                          </PTypography>
                          {NEW_CONSTRUCTION ? (
                            <PlannedBuildingLabel
                              isNewConstruction={item.isNewConstruction ?? false}
                              isMissingEnergyData={Boolean(item.isMissingEnergyData)}
                            />
                          ) : null}
                        </Stack>
                      }
                      secondary={
                        item.secondary ? (
                          <PTypography variant="caption" color="secondary">
                            {item.secondary}
                          </PTypography>
                        ) : undefined
                      }
                    />
                  </ListItem>
                );

                return (
                  <Fragment key={item.value}>
                    {listItem}
                    {item.hasBottomDivider ? <Divider sx={{ my: 1, borderWidth: '1px' }} /> : null}
                  </Fragment>
                );
              })}
            </List>
          ) : isLoading ? (
            <Stack width="100%" justifyContent="center" alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Stack width={'100%'} justifyContent={'center'} alignItems={'center'}>
              <PTypography
                variant="body1"
                color="secondary"
                sx={{
                  padding: 3,
                }}
              >
                {noDataText}
              </PTypography>
            </Stack>
          )}
        </InfiniteScroll>
      </Box>
      {bottomAction ? (
        <>
          <Divider />
          <Stack direction="row" alignItems="center" p={1} sx={{ background: theme.palette.common.white, height: 48 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                bottomAction.onClick();
                onHideSearchBox?.({}, 'backdropClick');
              }}
            >
              {bottomAction.label}
            </Button>
          </Stack>
        </>
      ) : null}
      {isMultiSelect ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            background: theme.palette.common.white,
            height: 48,
            pr: 3,
          }}
        >
          <Button variant="text" size="small" onClick={handleClear} disabled={!showClearButton}>
            {t('General_ClearSelection')}
          </Button>
          {handleClick ? (
            <Button variant="contained" size="medium" onClick={handleClick} disabled={disableApplyButton}>
              {t('General_Apply')}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </MenuPopover>
  );
};

export default SearchBoxWithLazyLoading;
